import { formatIsoDate, toUtcDate } from './calendar';

const cache = new Map<number, ReadonlySet<string>>();

export type SwedishHoliday = {
  date: string;
  name: string;
  statutory: boolean;
};

function computeEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monthIndex0 = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return toUtcDate(year, monthIndex0, day);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

function findSaturday(year: number, month: number, fromDay: number, toDay: number): Date {
  for (let day = fromDay; day <= toDay; day += 1) {
    const candidate = toUtcDate(year, month, day);
    if (candidate.getUTCDay() === 6) {
      return candidate;
    }
  }
  throw new Error(`No saturday found in ${year}-${month} between ${fromDay} and ${toDay}`);
}

export function getSwedishHolidayDetails(year: number): SwedishHoliday[] {
  if (!Number.isInteger(year) || year < 1900 || year > 2200) {
    throw new RangeError(`Year out of supported range: ${year}`);
  }

  const easter = computeEasterSunday(year);
  const longFriday = addDays(easter, -2);
  const easterSaturday = addDays(easter, -1);
  const easterMonday = addDays(easter, 1);
  const ascension = addDays(easter, 39);
  const pentecost = addDays(easter, 49);
  const midsummerDay = findSaturday(year, 6, 20, 26);
  const midsummerEve = addDays(midsummerDay, -1);
  const oct31 = toUtcDate(year, 10, 31);
  const allSaintsDay = oct31.getUTCDay() === 6 ? oct31 : findSaturday(year, 11, 1, 6);

  const list: SwedishHoliday[] = [
    { date: formatIsoDate(toUtcDate(year, 1, 1)), name: 'Nyårsdagen', statutory: true },
    { date: formatIsoDate(toUtcDate(year, 1, 6)), name: 'Trettondedag jul', statutory: true },
    { date: formatIsoDate(longFriday), name: 'Långfredag', statutory: true },
    { date: formatIsoDate(easterSaturday), name: 'Påskafton', statutory: false },
    { date: formatIsoDate(easter), name: 'Påskdagen', statutory: true },
    { date: formatIsoDate(easterMonday), name: 'Annandag påsk', statutory: true },
    { date: formatIsoDate(toUtcDate(year, 5, 1)), name: 'Första maj', statutory: true },
    { date: formatIsoDate(ascension), name: 'Kristi himmelsfärds dag', statutory: true },
    { date: formatIsoDate(pentecost), name: 'Pingstdagen', statutory: true },
    { date: formatIsoDate(toUtcDate(year, 6, 6)), name: 'Sveriges nationaldag', statutory: true },
    { date: formatIsoDate(midsummerEve), name: 'Midsommarafton', statutory: false },
    { date: formatIsoDate(midsummerDay), name: 'Midsommardagen', statutory: true },
    { date: formatIsoDate(allSaintsDay), name: 'Alla helgons dag', statutory: true },
    { date: formatIsoDate(toUtcDate(year, 12, 24)), name: 'Julafton', statutory: false },
    { date: formatIsoDate(toUtcDate(year, 12, 25)), name: 'Juldagen', statutory: true },
    { date: formatIsoDate(toUtcDate(year, 12, 26)), name: 'Annandag jul', statutory: true },
    { date: formatIsoDate(toUtcDate(year, 12, 31)), name: 'Nyårsafton', statutory: false },
  ];

  list.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return list;
}

export function getSwedishHolidays(year: number): ReadonlySet<string> {
  const cached = cache.get(year);
  if (cached) return cached;
  const set = new Set(getSwedishHolidayDetails(year).map((h) => h.date));
  const frozen: ReadonlySet<string> = set;
  cache.set(year, frozen);
  return frozen;
}

export function isSwedishHoliday(isoDate: string): boolean {
  const year = Number.parseInt(isoDate.slice(0, 4), 10);
  if (!Number.isFinite(year)) return false;
  return getSwedishHolidays(year).has(isoDate);
}

export function getSwedishHolidayName(isoDate: string): string | null {
  const year = Number.parseInt(isoDate.slice(0, 4), 10);
  if (!Number.isFinite(year)) return null;
  const match = getSwedishHolidayDetails(year).find((h) => h.date === isoDate);
  return match ? match.name : null;
}

export function getSwedishHolidaysInRange(fromIso: string, toIso: string): SwedishHoliday[] {
  if (fromIso > toIso) return [];
  const fromYear = Number.parseInt(fromIso.slice(0, 4), 10);
  const toYear = Number.parseInt(toIso.slice(0, 4), 10);
  const result: SwedishHoliday[] = [];
  for (let year = fromYear; year <= toYear; year += 1) {
    for (const h of getSwedishHolidayDetails(year)) {
      if (h.date >= fromIso && h.date <= toIso) result.push(h);
    }
  }
  return result;
}
